import React, { ReactNode } from 'react'

import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/react'
import { UiCloseIcon, UiSearchIcon } from '@octane/spark'
import { Field, FieldProps, getIn } from 'formik'
import { useTheme } from 'styled-components'

import { ErrorMessage } from '../FormikDropdown/FormikDropdown.styled'
import {
    ClearButton,
    EmptyState,
    Field as StyledField,
    InputWrap,
    Option,
    OptionMeta,
    OptionsPanel,
    PrefilledField,
    SearchButton,
    StyledInput,
    Wrapper,
} from './FormikCombobox.styled'

export interface FormikComboboxChild {
    name: string
    value: string
    meta?: string
}

export interface FormikComboboxProps {
    name: string
    ariaLabel?: string
    placeholder?: string
    error?: boolean
    prefilled?: boolean
    disabled?: boolean
    loading?: boolean
    query: string
    onQueryChange: (query: string) => void
    onChange?: (value: string) => void
    onClear?: () => void
    onFocus?: () => void
    displayValue: (value: string) => string
    children: FormikComboboxChild[]
    emptyTitle?: ReactNode
    emptyMessage?: ReactNode
    tabIndex?: number
}

function highlightWords(text: string, words: string[]) {
    if (!words.length) return text

    const pattern = words
        .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('|')

    const parts = text.split(new RegExp(`(${pattern})`, 'gi'))

    return parts.map((part, index) =>
        words.some((word) => part.toLowerCase() === word.toLowerCase()) ? (
            <mark key={index}>{part}</mark>
        ) : (
            part
        ),
    )
}

export default function FormikCombobox({
    name,
    ariaLabel,
    placeholder = 'Select',
    error = false,
    prefilled = false,
    disabled = false,
    loading = false,
    query,
    onQueryChange,
    onChange = () => undefined,
    onClear,
    onFocus,
    displayValue,
    children,
    emptyTitle = 'No results',
    emptyMessage = 'No search result found.',
    tabIndex,
}: Readonly<FormikComboboxProps>) {
    const queryWords = query.toLowerCase().split(/\s+/).filter(Boolean)
    const theme = useTheme()

    return (
        <Wrapper>
            <Field name={name}>
                {({ field, form }: FieldProps) => {
                    const touched = getIn(form.touched, field.name)
                    const formikErrorMessage = getIn(form.errors, field.name)
                    const hasError = touched && !!(formikErrorMessage || error)

                    if (prefilled) {
                        const label = displayValue(field.value)

                        return (
                            <PrefilledField isPlaceholder={!label}>
                                {label || placeholder}
                            </PrefilledField>
                        )
                    }

                    const handleSelect = (value: string | null) => {
                        form.setFieldValue(name, value ?? '')
                        onChange(value ?? '')
                        onQueryChange('')
                    }

                    const handleClear = () => {
                        form.setFieldValue(name, '')
                        onQueryChange('')
                        onClear?.()
                    }

                    return (
                        <>
                            <Combobox
                                value={field.value || null}
                                onChange={handleSelect}
                                onClose={() => onQueryChange('')}
                                disabled={disabled}
                                immediate
                            >
                                <StyledField
                                    error={hasError}
                                    disabled={disabled}
                                >
                                    <InputWrap>
                                        <ComboboxInput
                                            as={StyledInput}
                                            data-testid={name}
                                            placeholder={placeholder}
                                            displayValue={(
                                                value: string | null,
                                            ) =>
                                                value ? displayValue(value) : ''
                                            }
                                            onChange={(event) =>
                                                onQueryChange(
                                                    event.target.value,
                                                )
                                            }
                                            onBlur={() => {
                                                form.setFieldTouched(name, true)
                                            }}
                                            onFocus={onFocus}
                                            aria-label={
                                                ariaLabel || placeholder
                                            }
                                            autoComplete="off"
                                            disabled={disabled}
                                            tabIndex={tabIndex}
                                        />
                                        {!!field.value && !disabled && (
                                            <ClearButton
                                                type="button"
                                                aria-label="Clear selection"
                                                onClick={handleClear}
                                            >
                                                <UiCloseIcon
                                                    width={12}
                                                    height={12}
                                                    color={
                                                        theme.colors.monochrome
                                                            .graphite
                                                    }
                                                />
                                            </ClearButton>
                                        )}
                                    </InputWrap>
                                    <ComboboxButton
                                        as={SearchButton}
                                        type="button"
                                        aria-label="Search"
                                        disabled={disabled}
                                        tabIndex={-1}
                                    >
                                        <UiSearchIcon
                                            color="#FFFFFF"
                                            width={22}
                                            height={22}
                                        />
                                    </ComboboxButton>
                                </StyledField>

                                <ComboboxOptions
                                    as={OptionsPanel}
                                    anchor={{ gap: 5, to: 'bottom start' }}
                                    data-testid={`${name}-combobox-options`}
                                >
                                    {loading && (
                                        <EmptyState>Loading…</EmptyState>
                                    )}

                                    {!loading && children.length === 0 && (
                                        <EmptyState>
                                            <strong>{emptyTitle}</strong>
                                            {emptyMessage}
                                        </EmptyState>
                                    )}

                                    {!loading &&
                                        children.map((child) => (
                                            <ComboboxOption
                                                key={child.value}
                                                value={child.value}
                                            >
                                                {({ focus, selected }) => (
                                                    <Option
                                                        focus={focus}
                                                        selected={selected}
                                                    >
                                                        <span>
                                                            {highlightWords(
                                                                child.name,
                                                                queryWords,
                                                            )}
                                                        </span>
                                                        {child.meta && (
                                                            <OptionMeta>
                                                                {highlightWords(
                                                                    child.meta,
                                                                    queryWords,
                                                                )}
                                                            </OptionMeta>
                                                        )}
                                                    </Option>
                                                )}
                                            </ComboboxOption>
                                        ))}
                                </ComboboxOptions>
                            </Combobox>

                            {!!(touched && formikErrorMessage) && (
                                <ErrorMessage>
                                    {formikErrorMessage}
                                </ErrorMessage>
                            )}
                        </>
                    )
                }}
            </Field>
        </Wrapper>
    )
}
